import { useRef, useState } from "react";
import { AlertCircle, CheckCircle2, Info, X, AlertTriangle } from "lucide-react";
import { useToastStore, type ToastItem, type ToastTone } from "@/store/toastStore";
import { Button } from "./Button";
import { cn } from "@/utils/format";

const icons: Record<ToastTone, typeof Info> = {
  success: CheckCircle2,
  error: AlertCircle,
  warning: AlertTriangle,
  info: Info,
};

function ToastCard({ item, onDismiss }: { item: ToastItem; onDismiss: (id: string) => void }) {
  const Icon = icons[item.tone];
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const durationMs = item.durationMs ?? 4200;

  const runAction = async () => {
    if (!item.action || busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    try {
      await item.action.onClick();
    } catch {
      busyRef.current = false;
      setBusy(false);
    }
  };

  return (
    <div className={cn("toast", `toast--${item.tone}`, item.progress && "toast--progress")} role="status">
      <span className="toast__icon" aria-hidden>
        <Icon size={18} />
      </span>
      <div className="toast__body">
        <strong>{item.title}</strong>
        {item.description ? <p>{item.description}</p> : null}
        {item.action ? (
          <div className="toast__actions">
            <button
              type="button"
              className="toast__undo"
              onClick={() => void runAction()}
              disabled={busy}
            >
              {busy ? "Restoring…" : item.action.label}
            </button>
          </div>
        ) : null}
        {item.progress ? (
          <div className="toast__progress" aria-hidden>
            <span
              className="toast__progress-bar"
              style={{ animationDuration: `${durationMs}ms` }}
            />
          </div>
        ) : null}
      </div>
      <Button
        variant="ghost"
        size="icon"
        aria-label="Dismiss notification"
        onClick={() => onDismiss(item.id)}
        disabled={busy}
      >
        <X size={14} />
      </Button>
    </div>
  );
}

export function ToastHost() {
  const toasts = useToastStore((s) => s.toasts);
  const dismiss = useToastStore((s) => s.dismiss);

  if (!toasts.length) return null;

  return (
    <div className="toast-host" aria-live="polite" aria-relevant="additions">
      {toasts.map((item) => (
        <ToastCard key={item.id} item={item} onDismiss={dismiss} />
      ))}
    </div>
  );
}
